// Help content for appointment features
export const appointmentHelp = {
  // Main appointments section
  appointmentsOverview: {
    title: "Lead Appointments",
    content: (
      <div className="space-y-2">
        <p>Schedule appointments with leads before they become patients.</p>
        <div className="text-xs space-y-1">
          <p><strong>✅ Best Practice:</strong> Schedule consultations soon after lead contact</p>
          <p><strong>📱 Auto-confirm:</strong> Sends instant SMS/email confirmations</p>
          <p><strong>🔔 Reminders:</strong> Send follow-up reminders before appointments</p>
        </div>
      </div>
    )
  },

  // Scheduling form help
  scheduleAppointment: {
    title: "Schedule Appointment",
    content: (
      <div className="space-y-2">
        <p>Create a new appointment for this lead.</p>
        <div className="text-xs space-y-1">
          <p><strong>Date/Time:</strong> Only future dates allowed</p>
          <p><strong>Type:</strong> Choose "Consultation" for new leads</p>
          <p><strong>Staff:</strong> Optional - assign to specific team member</p>
          <p><strong>Confirmation:</strong> Recommended - sends automatic SMS/email</p>
        </div>
      </div>
    )
  },

  // Appointment types
  appointmentTypes: {
    title: "Appointment Types",
    content: (
      <div className="space-y-1 text-xs">
        <p><strong>Consultation:</strong> Initial meeting (recommended for leads)</p>
        <p><strong>Follow-up:</strong> Secondary meeting</p>
        <p><strong>Treatment:</strong> Actual dental procedure</p>
        <p><strong>Cleaning:</strong> Routine cleaning</p>
        <p><strong>Exam:</strong> Dental examination</p>
        <p><strong>Emergency:</strong> Urgent care</p>
      </div>
    )
  },

  // Duration help
  appointmentDuration: {
    title: "Appointment Duration",
    content: "How long the appointment is expected to last. Default is 1 hour for consultations. Adjust based on appointment type and complexity."
  },

  // Staff assignment
  staffAssignment: {
    title: "Staff Assignment",
    content: (
      <div className="space-y-2">
        <p>Optionally assign this appointment to a specific staff member.</p>
        <div className="text-xs space-y-1">
          <p><strong>Benefits:</strong> Personal service, calendar management</p>
          <p><strong>Optional:</strong> Can be assigned later</p>
          <p><strong>Permissions:</strong> Only staff from your locations shown</p>
        </div>
      </div>
    )
  },

  // Confirmation checkbox
  sendConfirmation: {
    title: "Send Confirmation",
    content: (
      <div className="space-y-2">
        <p>Automatically send appointment confirmation to the lead.</p>
        <div className="text-xs space-y-1">
          <p><strong>📱 SMS:</strong> Instant text message (if phone available)</p>
          <p><strong>📧 Email:</strong> Professional branded email</p>
          <p><strong>✅ Recommended:</strong> Always enable for new appointments</p>
        </div>
      </div>
    )
  },

  // Appointment statuses
  appointmentStatuses: {
    title: "Appointment Statuses",
    content: (
      <div className="space-y-1 text-xs">
        <p><span className="inline-block w-3 h-3 rounded-full bg-blue-400 mr-2"></span><strong>Scheduled:</strong> Created, awaiting confirmation</p>
        <p><span className="inline-block w-3 h-3 rounded-full bg-green-400 mr-2"></span><strong>Confirmed:</strong> Lead confirmed attendance</p>
        <p><span className="inline-block w-3 h-3 rounded-full bg-gray-400 mr-2"></span><strong>Completed:</strong> Appointment finished</p>
        <p><span className="inline-block w-3 h-3 rounded-full bg-red-400 mr-2"></span><strong>No Show:</strong> Lead didn't attend</p>
        <p><span className="inline-block w-3 h-3 rounded-full bg-yellow-400 mr-2"></span><strong>Cancelled:</strong> Cancelled by either party</p>
        <p><span className="inline-block w-3 h-3 rounded-full bg-purple-400 mr-2"></span><strong>Rescheduled:</strong> Moved to different time</p>
      </div>
    )
  },

  // Send reminder action
  sendReminder: {
    title: "Send Appointment Reminder",
    content: (
      <div className="space-y-2">
        <p>Send a reminder message to the lead about their upcoming appointment.</p>
        <div className="text-xs space-y-1">
          <p><strong>Best Time:</strong> 24-48 hours before appointment</p>
          <p><strong>Multi-channel:</strong> Tries both SMS and email</p>
          <p><strong>Professional:</strong> Includes all appointment details</p>
        </div>
      </div>
    )
  },

  // Send confirmation action
  sendAppointmentConfirmation: {
    title: "Send Appointment Confirmation", 
    content: (
      <div className="space-y-2">
        <p>Send a confirmation message with appointment details.</p>
        <div className="text-xs space-y-1">
          <p><strong>When:</strong> Right after scheduling or when requested</p>
          <p><strong>Content:</strong> Date, time, location, contact info</p>
          <p><strong>Tracking:</strong> Marked as sent to prevent duplicates</p>
        </div>
      </div>
    )
  },

  // Update status
  updateStatus: {
    title: "Update Appointment Status",
    content: (
      <div className="space-y-2">
        <p>Change the appointment status to track progress.</p>
        <div className="text-xs space-y-1">
          <p><strong>Keep Current:</strong> Update status as situation changes</p>
          <p><strong>Reporting:</strong> Status used for analytics and follow-up</p>
          <p><strong>Workflow:</strong> Scheduled → Confirmed → Completed</p>
        </div>
      </div>
    )
  },

  // Delete appointment
  deleteAppointment: {
    title: "Delete Appointment",
    content: (
      <div className="space-y-2">
        <p className="text-yellow-200">⚠️ Permanently delete this appointment.</p>
        <div className="text-xs space-y-1">
          <p><strong>Warning:</strong> Cannot be undone</p>
          <p><strong>Alternative:</strong> Consider "Cancelled" status instead</p>
          <p><strong>History:</strong> All related messages will remain</p>
        </div>
      </div>
    )
  },

  // Visual indicators
  visualIndicators: {
    title: "Appointment Indicators",
    content: (
      <div className="space-y-1 text-xs">
        <p><span className="inline-block px-2 py-1 bg-accent-teal-subtle text-teal-800 rounded text-xs mr-2">Upcoming</span>Future appointment</p>
        <p><span className="inline-block px-2 py-1 bg-red-100 text-red-800 rounded text-xs mr-2">Past Due</span>Scheduled time has passed</p>
        <p><span className="inline-block px-2 py-1 bg-green-100 text-green-800 rounded text-xs mr-2">✓ Sent</span>Communication sent successfully</p>
      </div>
    )
  },

  // Communication tracking
  communicationTracking: {
    title: "Communication Tracking",
    content: (
      <div className="space-y-2">
        <p>All appointment communications are automatically tracked.</p>
        <div className="text-xs space-y-1">
          <p><strong>Message Log:</strong> View in lead's message history</p>
          <p><strong>Status Tracking:</strong> See if reminders were sent</p>
          <p><strong>Error Handling:</strong> Failed messages logged for review</p>
        </div>
      </div>
    )
  },

  // Troubleshooting
  troubleshooting: {
    title: "Common Issues",
    content: (
      <div className="space-y-2">
        <div className="text-xs space-y-1">
          <p><strong>Not showing appointments?</strong></p>
          <p className="ml-2">• Refresh the page or reopen lead</p>
          
          <p><strong>SMS/Email not sending?</strong></p>
          <p className="ml-2">• Check lead has valid phone/email</p>
          <p className="ml-2">• Verify location messaging is enabled</p>
          
          <p><strong>Can't schedule?</strong></p>
          <p className="ml-2">• Ensure you have staff permissions</p>
          <p className="ml-2">• Verify lead is in your location</p>
        </div>
      </div>
    )
  }
};

// Help content for messaging features
export const messagingHelp = {
  // Main messaging overview
  messagingOverview: {
    title: "Lead Messaging System",
    content: (
      <div className="space-y-2">
        <p>Communicate with leads via SMS and email from one interface.</p>
        <div className="text-xs space-y-1">
          <p>
            <strong>📱 Multi-channel:</strong> Automatic SMS and email support
          </p>
          <p>
            <strong>⚡ Real-time:</strong> Instant messaging with auto-refresh
          </p>
          <p>
            <strong>📝 Templates:</strong> Pre-written messages for quick
            responses
          </p>
          <p>
            <strong>📊 Tracking:</strong> Complete message history and status
          </p>
        </div>
      </div>
    ),
  },

  // Message types
  messageTypes: {
    title: "Message Types",
    content: (
      <div className="space-y-2">
        <p>Choose the best communication channel for your lead.</p>
        <div className="text-xs space-y-1">
          <p>
            <strong>📱 SMS:</strong> Instant, high open rates, perfect for
            appointments
          </p>
          <p>
            <strong>📧 Email:</strong> Professional, detailed information,
            attachments
          </p>
          <p>
            <strong>🎯 Auto-select:</strong> System picks best option based on
            availability
          </p>
        </div>
      </div>
    ),
  },

  // Template variables
  templateVariables: {
    title: "Template Variables",
    content: (
      <div className="space-y-2">
        <p>Templates automatically substitute lead information:</p>
        <div className="text-xs space-y-1">
          <p>
            <code className="bg-background px-1 rounded">
              {"{{first_name}}"}
            </code>{" "}
            → Lead's first name
          </p>
          <p>
            <code className="bg-background px-1 rounded">
              {"{{last_name}}"}
            </code>{" "}
            → Lead's last name
          </p>
          <p>
            <code className="bg-background px-1 rounded">
              {"{{full_name}}"}
            </code>{" "}
            → Complete name
          </p>
          <p>
            <code className="bg-background px-1 rounded">{"{{phone}}"}</code> →
            Phone number
          </p>
          <p>
            <code className="bg-background px-1 rounded">{"{{email}}"}</code> →
            Email address
          </p>
        </div>
      </div>
    ),
  },

  // Message composition
  messageComposition: {
    title: "Message Composition",
    content: (
      <div className="space-y-2">
        <p>Tips for effective lead communication:</p>
        <div className="text-xs space-y-1">
          <p>
            <strong>✅ Be Personal:</strong> Use their name and reference their
            needs
          </p>
          <p>
            <strong>📏 Keep SMS Short:</strong> Under 160 characters for best
            results
          </p>
          <p>
            <strong>📧 Email Details:</strong> More comprehensive information
            welcome
          </p>
          <p>
            <strong>⏰ Timing:</strong> Consider business hours for professional
            leads
          </p>
        </div>
      </div>
    ),
  },

  // Message status indicators
  messageStatuses: {
    title: "Message Status Indicators",
    content: (
      <div className="space-y-1 text-xs">
        <p>
          <span className="inline-flex items-center text-green-600 mr-2">
            <svg
              className="h-3 w-3 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            Sent
          </span>
          Message delivered successfully
        </p>
        <p>
          <span className="inline-flex items-center text-red-600 mr-2">
            <svg
              className="h-3 w-3 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            Failed
          </span>
          Delivery failed - check contact info
        </p>
        <p>
          <span className="inline-block w-3 h-3 rounded-full bg-blue-400 mr-2"></span>
          <strong>Outbound:</strong> Messages you sent
        </p>
        <p>
          <span className="inline-block w-3 h-3 rounded-full bg-gray-400 mr-2"></span>
          <strong>Inbound:</strong> Messages received
        </p>
      </div>
    ),
  },

  // Message direction
  messageDirection: {
    title: "Message Direction",
    content: (
      <div className="space-y-2">
        <p>Understanding message flow:</p>
        <div className="text-xs space-y-1">
          <p>
            <strong>📤 Outbound:</strong> Messages you send to the lead (blue)
          </p>
          <p>
            <strong>📥 Inbound:</strong> Messages received from the lead (gray)
          </p>
          <p>
            <strong>🔄 Auto-read:</strong> Inbound messages marked as read
            automatically
          </p>
        </div>
      </div>
    ),
  },

  // Refresh messages
  refreshMessages: {
    title: "Refresh Messages",
    content: (
      <div className="space-y-2">
        <p>Keep your conversation up-to-date:</p>
        <div className="text-xs space-y-1">
          <p>
            <strong>🔄 Auto-refresh:</strong> Messages update every 2 minutes
          </p>
          <p>
            <strong>📱 Manual refresh:</strong> Click refresh button anytime
          </p>
          <p>
            <strong>⚡ Real-time:</strong> New outbound messages appear
            instantly
          </p>
        </div>
      </div>
    ),
  },

  // Messaging availability
  messagingAvailability: {
    title: "Messaging Availability",
    content: (
      <div className="space-y-2">
        <p>When messaging is available:</p>
        <div className="text-xs space-y-1">
          <p>
            <strong>✅ Location Enabled:</strong> Location must have messaging
            enabled
          </p>
          <p>
            <strong>📞 Contact Info:</strong> Lead needs phone (SMS) or email
            address
          </p>
          <p>
            <strong>🔑 Permissions:</strong> User must have messaging privileges
          </p>
          <p>
            <strong>⚙️ Service Setup:</strong> Twilio/Mailgun must be configured
          </p>
        </div>
      </div>
    ),
  },

  // Troubleshooting
  messagingTroubleshooting: {
    title: "Messaging Issues",
    content: (
      <div className="space-y-2">
        <div className="text-xs space-y-1">
          <p>
            <strong>Messages not sending?</strong>
          </p>
          <p className="ml-2">• Verify phone/email format is correct</p>
          <p className="ml-2">• Check location messaging is enabled</p>
          <p className="ml-2">• Ensure service credentials are configured</p>

          <p>
            <strong>Not seeing new messages?</strong>
          </p>
          <p className="ml-2">• Click refresh button to update</p>
          <p className="ml-2">• Check internet connection</p>

          <p>
            <strong>Templates not loading?</strong>
          </p>
          <p className="ml-2">• Switch message types to refresh templates</p>
          <p className="ml-2">• Contact admin if templates are missing</p>
        </div>
      </div>
    ),
  },
};

// Quick tips for different contexts
export const appointmentTips = {
  scheduling: "💡 Tip: Enable auto-confirmation to instantly notify the lead",
  management: "💡 Tip: Send reminders 24-48 hours before appointments for best results",
  status: "💡 Tip: Keep statuses updated - they're used for reporting and follow-ups"
};

export const messagingTips = {
  composition: "💡 Tip: Use Enter to send quickly, Shift+Enter for line breaks",
  templates: "💡 Tip: Templates automatically fill in lead information using {{variables}}",
  timing: "💡 Tip: Send messages during business hours for better response rates"
};